import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Target, ChevronLeft, TrendingUp, Users, IndianRupee, Calendar } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { presetToRange } from './ReportFilters';

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';


export default function HealthCampROIReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { from, to } = presetToRange(preset, customFrom, customTo);
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to)   q.set('to', to);
        const qs = q.toString();
        const resp = await apiFetch(`engage/analytics/camp-roi/${qs ? '?' + qs : ''}`);
        const results = resp?.data?.data || resp?.results || resp?.data?.results || [];
        setRows(Array.isArray(results) ? results : []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load ROI report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, reloadTick]);

  const totals = rows.reduce((acc, r) => ({
    attended: acc.attended + (Number(r.actual_footfall) || 0),
    leads: acc.leads + (Number(r.enquiries_captured) || 0),
    converted: acc.converted + (Number(r.converted_patients) || 0),
    spend: acc.spend + Number(r.actual_spend || r.budget || 0),
  }), { attended: 0, leads: 0, converted: 0, spend: 0 });

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-sm">
          <Target className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Health Camp ROI</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Attendance, conversion %, cost-per-acquired-patient</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Attended</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{totals.attended}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Leads gen.</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{totals.leads}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Converted</span>
          </div>
          <div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">
            {totals.converted}
            <span className="text-xs text-gray-500 ml-1">({totals.leads ? Math.round(totals.converted / totals.leads * 100) : 0}%)</span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Cost / patient</span>
          </div>
          <div className="text-xl font-bold mt-1 text-amber-600 dark:text-amber-400">
            ₹ {formatINR(totals.converted ? totals.spend / totals.converted : 0)}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Camp</th>
              <th className="px-4 py-2 text-left font-medium">Date</th>
              <th className="px-4 py-2 text-right font-medium">Attended</th>
              <th className="px-4 py-2 text-right font-medium">Leads</th>
              <th className="px-4 py-2 text-right font-medium">Converted</th>
              <th className="px-4 py-2 text-right font-medium">Spend</th>
              <th className="px-4 py-2 text-right font-medium">Cost / patient</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No health camps yet.</td></tr>
            ) : rows.map(r => {
              const spend = Number(r.actual_spend || r.budget || 0);
              const cpp = r.converted_patients > 0 ? spend / r.converted_patients : 0;
              return (
                <tr key={r.camp_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5">
                    <Link to={`/engage/health-camps/${r.camp_id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {r.camp_name}
                    </Link>
                    {r.specialty_focus && <div className="text-[11px] text-gray-500 dark:text-gray-400">{r.specialty_focus}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-600 dark:text-gray-300">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmt(r.start_datetime)}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-white">{r.actual_footfall || 0}</td>
                  <td className="px-4 py-2.5 text-right text-blue-600 dark:text-blue-400">{r.enquiries_captured || 0}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400 font-medium">{r.converted_patients || 0}</td>
                  <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">₹ {formatINR(spend)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-amber-600 dark:text-amber-400">₹ {formatINR(cpp)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
